<template>
  <div class="dg" id="rally-options-container">
    <div class="body rally-details-page">
      <div class="rally-details-header">
        <h2 class="page-title">Rally Details</h2>
        <button class="save-changes-btn" type="button" @click="updateRallyData" v-if="breakpoint.currentSize != 'mobile'">
          Save Changes 
        </button>
      </div>

      <div class="rally-details-card">
        <div class="avatar-upload-container">
          <AvatarUpload :uniqueId="uniqueId" :rallyImg="avatarImage" />
        </div>

        <form @submit.prevent="updateRallyData" class="rally-options-form">
          <div class="form-group"> 
            <label for="rally-name" class="form-label">Rally Name</label>
            <input
              id="rally-name"
              v-model="rallyTitle"
              type="text"
              placeholder="My Awesome Rally"
              class="form-control"
              style="height: 40px"
            />
          </div>

          <div class="form-group">
            <div class="label-with-counter">
              <label for="rally-cry" class="form-label">Rally Cry</label>
              <span class="char-counter">{{ rallyCryLength }}/300</span>
            </div>
            <textarea
              id="rally-cry"
              v-model="rallyDescription"
              rows="3"
              class="form-control textarea"
            ></textarea>
          </div>

          <div class="form-group">
            <div class="label-with-counter">
              <label for="invitation-msg" class="form-label">Default Invitation Message</label>
              <span class="char-counter">{{ invitationMessageLength }}/300</span>
            </div>
            <textarea
              id="invitation-msg"
              v-model="rallyInvitationMessage"
              rows="4"
              class="form-control textarea"
            ></textarea>
          </div>

          <div class="form-section">
            <h3 class="section-heading">Membership Settings</h3>
            <div class="radio-group">
              <label class="radio-option" :class="{ selected: privacyValue === 'Public' }">
                <input
                  type="radio"
                  name="privacy"
                  value="Public"
                  v-model="privacyValue"
                  class="radio-input"
                />
                <span class="custom-radio"></span>
                <div class="radio-content">
                  <span class="radio-title">Public</span>
                  <span class="radio-desc">Anyone who is interested can join.</span>
                </div>
              </label>

              <label class="radio-option" :class="{ selected: privacyValue === 'Private' }">
                <input
                  type="radio"
                  name="privacy"
                  value="Private"
                  v-model="privacyValue"
                  class="radio-input"
                />
                <span class="custom-radio"></span>
                <div class="radio-content">
                  <span class="radio-title">Private</span>
                  <span class="radio-desc">Members must be invited or have their request approved by an admin.</span>
                </div>
              </label>

              <label class="radio-option" :class="{ selected: privacyValue === 'Unlisted' }">
                <input
                  type="radio"
                  name="privacy"
                  value="Unlisted"
                  v-model="privacyValue"
                  class="radio-input"
                />
                <span class="custom-radio"></span>
                <div class="radio-content">
                  <span class="radio-title">Unlisted</span>
                  <span class="radio-desc">Members may only be invited by an admin. This Rally will not appear in search results.</span>
                </div>
              </label>
            </div>
          </div>

          <div class="form-section">
            <h3 class="section-heading">Immediate Notification Permissions</h3>
            <p class="section-subtitle">Select the immediate notification permissions granted by user type.</p>

            <div class="radio-group">
              <label class="radio-option" :class="{ selected: notifyValue === 'true' || notifyValue === true }">
                <input
                  type="radio"
                  name="disable_notify_immediate"
                  value="true"
                  v-model="notifyValue"
                  class="radio-input"
                />
                <span class="custom-radio"></span>
                <div class="radio-content">
                  <span class="radio-title">Admin only (Recommended for most groups)</span>
                  <span class="radio-desc">New messages are normally grouped into the Daily Digest email sent at the end of each day. Only Admin are allowed to post urgent messages which are emailed immediately.</span>
                </div>
              </label>

              <label class="radio-option" :class="{ selected: notifyValue === 'false' || notifyValue === false }">
                <input
                  type="radio"
                  name="disable_notify_immediate"
                  value="false"
                  v-model="notifyValue"
                  class="radio-input"
                />
                <span class="custom-radio"></span>
                <div class="radio-content">
                  <span class="radio-title">All Members</span>
                  <span class="radio-desc">Allow all members to post urgent messages.</span>
                </div>
              </label>
            </div>
          </div>
        </form>
      </div>

      <div class="bottom-button-ctr" v-if="breakpoint.isMobile">
        <button class="save-changes-btn" type="button" @click="updateRallyData">
          Save Changes 
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { eventBus } from "@/main";
import { mapActions, mapState } from "vuex";
import UxMixin from "@/components/UxMixin.vue";
import { avaUrl } from "../../common";
import { useBreakpoint } from "../../composables/useBreakpoint"

export default {
  mixins: [UxMixin],
  name: "RallyOptions",
  props: ['rallyId', 'isDesktopSize'],
  data: {
    breakpoint: useBreakpoint(),
  },
  computed: {
    ...mapState("rallies", ["allRalliesByRallyId"]),
    ...mapState("contents", ["contentById", "defaultAvatarsByType"]),
    avatarImage() {
      if (this.contentById[this.rallyId + "_rally_avatar"]) {
        return this.contentById[this.rallyId + "_rally_avatar"].attachmentDataURL;
      } else {
        return avaUrl(this.rallyAvatarContent);
      }
    },
    rallyAvatarContent() {
      var avid = this.rally?.rally?.relationships?.avatar?.data?.id;
      if (avid == -1 || !this.contentById[avid]) {
        return this.defaultAvatarsByType["rally"];
      } else {
        return this.contentById[avid];
      }
    },
    rally() {
      return this.allRalliesByRallyId[this.rallyId] || {};
    },
    uniqueId() {
      return this.rallyId;
    },
    rallyTitle: {
      get() {
        return this.rally?.rally?.attributes?.['title'] || '';
      },
      set(val) {
        if (this.rally?.rally?.attributes) {
          this.rally.rally.attributes['title'] = val;
        }
      }
    },
    rallyDescription: {
      get() {
        return this.rally?.rally?.attributes?.['description'] || '';
      },
      set(val) {
        if (this.rally?.rally?.attributes) {
          this.rally.rally.attributes['description'] = val;
        }
      }
    },
    rallyInvitationMessage: {
      get() {
        return this.rally?.rally?.attributes?.['invitation-message'] || '';
      },
      set(val) {
        if (this.rally?.rally?.attributes) {
          this.rally.rally.attributes['invitation-message'] = val;
        }
      }
    },
    privacyValue: {
      get() {
        return this.rally?.rally?.attributes?.['privacy'] || 'Public';
      },
      set(val) {
        if (this.rally?.rally?.attributes) {
          this.rally.rally.attributes['privacy'] = val;
        }
      }
    },
    notifyValue: {
      get() {
        const val = this.rally?.rally?.attributes?.['disable-notify-immediate'];
        return val !== undefined ? String(val) : 'true';
      },
      set(val) {
        if (this.rally?.rally?.attributes) {
          this.rally.rally.attributes['disable-notify-immediate'] = val;
        }
      }
    },
    rallyCryLength() {
      return (this.rallyDescription || '').length;
    },
    invitationMessageLength() {
      return (this.rallyInvitationMessage || '').length;
    }
  },
  methods: {
    ...mapActions("rallies", ["updateRally"]),
    async updateRallyData() {
      eventBus.emit('uploadAvatar' + this.uniqueId, { rallyId: this.rallyId });
      const rally = this.rally;
      const privacy = rally.rally.attributes.privacy;
      const status = rally.rally.attributes.status;
      const notify_immediately = rally.rally.attributes['disable-notify-immediate'];
      const description = rally.rally.attributes['description'];
      const invitation_message = rally.rally.attributes['invitation-message'];

      await this.updateRally({
        rally: rally,
        privacy: privacy,
        status: status,
        disable_notify_immediate: notify_immediately,
        description: description,
        invitation_message: invitation_message
      });
      this.$store.dispatch('rallies_v3/refreshCurrentRallyData');
      this.$store.dispatch('user/flashSuccess', 'Rally successfully updated!');
    }
  }
};
</script>

<style scoped>

</style>